import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  Timestamp,
  query,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { uploadMultipleImages, deleteImage } from '../../utils/imageUpload';
import CategoryDropdown from './CategoryDropdown';
import Toast, { ToastType } from './Toast';
import FileUploadZone from '../FileUploadZone';
import { FiEdit, FiTrash2, FiArrowLeft, FiX } from 'react-icons/fi';

interface Product {
  id: string;
  category_id: string; // Firestore document ID (string)
  name: string;
  description: string;
  inStock: boolean; // Whether product is in stock
  price?: number; // Optional price
  views_count: number;
  images: Array<{ id: number; url: string }>;
  created_at?: Timestamp;
  updated_at?: Timestamp;
}

interface Category {
  id: string; // Firestore document ID
  name: string;
}

const ProductManager: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    inStock: true, // Default to in stock
    price: 0,
    priceDisplay: '', // For formatted display
    images: [] as string[],
  });
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [initialFormData, setInitialFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    inStock: true,
    price: 0,
    priceDisplay: '',
    images: [] as string[],
  });

  useEffect(() => {
    loadData();
  }, []);

  const isFormDirty = (): boolean => {
    if (
      formData.category_id !== initialFormData.category_id ||
      formData.name !== initialFormData.name ||
      formData.description !== initialFormData.description ||
      formData.inStock !== initialFormData.inStock ||
      formData.priceDisplay !== initialFormData.priceDisplay ||
      formData.images.length !== initialFormData.images.length ||
      imageFiles.length > 0
    ) {
      return true;
    }
    if (formData.images.some((url, i) => url !== initialFormData.images[i])) {
      return true;
    }
    return false;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsSnapshot, categoriesSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'products'), orderBy('created_at', 'desc'))),
        getDocs(collection(db, 'categories')),
      ]);

      const productsData = productsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];

      const categoriesData = categoriesSnapshot.docs.map((doc) => {
        const data = doc.data();
        // Use Firestore document ID (doc.id) as the category ID
        // Ignore any numeric 'id' field in the data - we use Firestore doc IDs now
        return {
          id: doc.id, // Firestore document ID - this is what we'll use for category_id
          name: data.name || '',
        };
      }) as Category[];
      
      console.log('Loaded categories:', categoriesData.map(c => ({ id: c.id, name: c.name })));

      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      setToast({ message: 'Lỗi khi tải dữ liệu', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageFilesSelected = (files: File[]) => {
    setImageFiles((prev) => [...prev, ...files]);
  };

  const removeImageFile = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeImageUrl = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate before uploading so we never store images if the form is invalid or user aborts
    if (!formData.category_id || formData.category_id.trim() === '') {
      setToast({ message: 'Vui lòng chọn danh mục', type: 'error' });
      return;
    }
    const selectedCategory = categories.find(cat => cat.id === formData.category_id);
    if (!selectedCategory) {
      setToast({ message: 'Vui lòng chọn danh mục hợp lệ. Danh mục không tồn tại.', type: 'error' });
      return;
    }

    let newImageUrls: string[] = [];
    try {
      setUploading(true);

      // Upload new images only after validation (so cancel before submit never uploads)
      if (imageFiles.length > 0) {
        const productId = editingId || 'temp';
        newImageUrls = await uploadMultipleImages(imageFiles, `products/${productId}`);
      }

      const allImageUrls = [...formData.images, ...newImageUrls];
      const images = allImageUrls.map((url, index) => ({
        id: index + 1,
        url,
      }));

      const categoryId = selectedCategory.id;
      const priceValue = parseFloat(formData.priceDisplay.replace(/,/g, '')) || formData.price;

      const productData: any = {
        category_id: categoryId,
        name: formData.name.trim(),
        description: formData.description.trim(),
        inStock: formData.inStock,
        views_count: editingId 
          ? products.find((p) => p.id === editingId)?.views_count || 0
          : 0,
        images,
        updated_at: Timestamp.now(),
      };
      if (priceValue > 0) {
        productData.price = priceValue;
      }

      if (editingId) {
        const productRef = doc(db, 'products', editingId);
        await updateDoc(productRef, productData);
        setToast({ message: 'Cập nhật sản phẩm thành công!', type: 'success' });
      } else {
        await addDoc(collection(db, 'products'), {
          ...productData,
          created_at: Timestamp.now(),
        });
        setToast({ message: 'Thêm sản phẩm thành công!', type: 'success' });
      }

      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Error saving product:', error);
      setToast({ message: error.message || 'Lỗi khi lưu sản phẩm', type: 'error' });
      // If we uploaded images but save failed, remove them from Storage so they are not orphaned
      if (newImageUrls.length > 0) {
        for (const url of newImageUrls) {
          try {
            await deleteImage(url);
          } catch (err) {
            console.warn('Failed to delete orphaned image:', url, err);
          }
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingId(product.id);
    const category = categories.find(cat => cat.id === product.category_id);
    const data = {
      category_id: category?.id || '',
      name: product.name,
      description: product.description,
      inStock: product.inStock !== undefined ? product.inStock : true,
      price: product.price || 0,
      priceDisplay: product.price ? formatPriceFromNumber(product.price) : '',
      images: product.images.map((img) => img.url),
    };
    setFormData(data);
    setInitialFormData(data);
    setImageFiles([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (product: Product) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa sản phẩm này?')) {
      return;
    }
    try {
      // Delete images from storage
      for (const image of product.images) {
        try {
          await deleteImage(image.url);
        } catch (error) {
          console.warn('Error deleting image:', error);
        }
      }

      // Delete product document
      await deleteDoc(doc(db, 'products', product.id));
      setToast({ message: 'Xóa sản phẩm thành công!', type: 'success' });
      loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      setToast({ message: 'Lỗi khi xóa sản phẩm', type: 'error' });
    }
  };

  const resetForm = () => {
    const empty = {
      category_id: '',
      name: '',
      description: '',
      inStock: true,
      price: 0,
      priceDisplay: '',
      images: [] as string[],
    };
    setEditingId(null);
    setFormData(empty);
    setInitialFormData(empty);
    setImageFiles([]);
  };

  const handleCancelClick = () => {
    if (isFormDirty() && !window.confirm('Bạn có thay đổi chưa lưu. Bạn có chắc muốn hủy?')) {
      return;
    }
    // Images are only uploaded on submit, so abort clears local state only; nothing to delete from Storage
    resetForm();
  };

  const getCategoryName = (categoryId: string | number) => {
    // categoryId can be string (Firestore doc ID) or number (legacy)
    const category = categories.find((c) => 
      c.id === String(categoryId) || c.id === categoryId
    );
    return category?.name || 'N/A';
  };

  const formatPrice = (value: string): string => {
    // Remove all non-digit characters (including dots and commas)
    const numericValue = value.replace(/\D/g, '');
    // Format with commas
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  const formatPriceFromNumber = (value: number): string => {
    // Convert number to string and format with commas
    const numericString = Math.floor(value).toString();
    return numericString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPrice(e.target.value);
    const numericValue = parseFloat(formatted.replace(/,/g, '')) || 0;
    setFormData({
      ...formData,
      price: numericValue,
      priceDisplay: formatted,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Quản lý Sản phẩm</h1>
        <Link
          to={window.location.hostname.startsWith('admin.') ? '/dashboard' : '/admin'}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <FiArrowLeft />
          <span>Quay lại</span>
        </Link>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          {editingId ? 'Chỉnh sửa Sản phẩm' : 'Thêm Sản phẩm Mới'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Danh mục *
            </label>
            <CategoryDropdown
              categories={categories}
              onCategoriesChange={loadData}
              value={formData.category_id}
              onChange={(categoryId) => setFormData((prev) => ({ ...prev, category_id: categoryId }))}
              onShowToast={(message, type) => setToast({ message, type })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên sản phẩm *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Ví dụ: Áo Phản Quang An Toàn"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mô tả
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Mô tả chi tiết về sản phẩm..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Giá (VNĐ)
              </label>
              <input
                type="text"
                value={formData.priceDisplay}
                onChange={handlePriceChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="1,000,000 (tùy chọn)"
              />
              <p className="mt-1 text-xs text-gray-500">
                Để trống nếu sản phẩm không có giá
              </p>
            </div>

            <div className="flex items-center">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.inStock}
                  onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Còn hàng
                </span>
              </label>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <FileUploadZone
              onFilesSelected={handleImageFilesSelected}
              accept="image/*"
              multiple
              maxSize={5 * 1024 * 1024}
              maxSizeLabel="5MB"
              label="Hình ảnh sản phẩm"
              hint="Có thể chọn nhiều hình ảnh. Kích thước tối đa: 5MB mỗi hình."
            />

            {/* All images (existing + new) */}
            {(formData.images.length > 0 || imageFiles.length > 0) && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Hình ảnh sản phẩm:</p>
                <div className="grid grid-cols-4 gap-4">
                  {/* Existing images */}
                  {formData.images.map((url, index) => (
                    <div key={`existing-${index}`} className="relative overflow-hidden rounded">
                      <img
                        src={url}
                        alt={`Image ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeImageUrl(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {/* New images */}
                  {imageFiles.map((file, index) => (
                    <div key={`new-${index}`} className="relative overflow-hidden rounded">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeImageFile(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Đang tải lên...' : editingId ? 'Cập nhật' : 'Thêm mới'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={handleCancelClick}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Hủy
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            Danh sách Sản phẩm ({products.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hình ảnh
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tên
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Danh mục
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Giá
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.images && product.images.length > 0 ? (
                      <div className="h-16 w-16 rounded overflow-hidden">
                        <img
                          src={product.images[0].url}
                          alt={product.name}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/64?text=No+Image';
                          }}
                        />
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {product.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {getCategoryName(product.category_id)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {product.price ? `${product.price.toLocaleString('vi-VN')}đ` : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {product.inStock ? (
                        <span className="text-green-600 font-medium">Còn hàng</span>
                      ) : (
                        <span className="text-red-600 font-medium">Hết hàng</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <FiEdit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(product)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Chưa có sản phẩm nào. Hãy thêm sản phẩm đầu tiên!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductManager;
